// Gateway client: WebSocket lifecycle, request envelope construction, and
// command canonical-byte production for one M6 session.
//
// The transport is injected (WebSocketLike factory) so the full client state
// machine is testable under node --test with an in-memory socket; main.tsx
// binds the browser's WebSocket (Node 24's global WebSocket has the same
// shape, so node-side E2E uses the same path).
//
// Command bytes: the client produces the canonical restricted-JSON command
// itself (submitCommand carries commandCanonicalJson and the gateway
// re-canonicalizes byte-for-byte), which is why restricted-json.ts was
// ported. commandId is the idempotency key: resubmission after a disconnect
// reuses the STORED canonical bytes verbatim — the server redelivers the
// journaled result for a committed command and rejects a stale-position one
// with REVISION_CONFLICT, so exactly-once holds across reconnects.

import {
  canonicalizeRestricted,
  parseRestrictedJson,
  type RestrictedValue,
} from "./restricted-json.js";
import { PUBLIC_RULESET } from "./identity.js";
import {
  ACTION_SCHEMA,
  CANONICALIZATION_PROFILE,
  COMMAND_SCHEMA,
  ENVELOPE_VERSION,
  ID_GRAMMAR,
  REQUEST_SCHEMA,
  parseInboundEnvelope,
  validateTransportErrorPayload,
  WireError,
  type RequestType,
  type SessionBoundPayload,
} from "./wire.js";
import { SessionStore, type CommandReceipt } from "./store.js";
import { importActionId, SGF_MAX_MOVES, type SgfMove } from "./sgf.js";
import { kindOf, type ActionKind } from "./codec.js";

// The structural minimum both the browser WebSocket and Node 24's global
// WebSocket satisfy (message events carry a `data` payload).
export interface WebSocketLike {
  send(data: string): void;
  close(): void;
  addEventListener(
    type: "open" | "message" | "close" | "error",
    listener: (event: { data?: unknown }) => void,
  ): void;
}
export type WebSocketFactory = (url: string) => WebSocketLike;

// The official game configuration is the only pair the gateway serves
// (session-profile.js): 19×19, one quota of each special kind per side.
export function officialGameConfig(): RestrictedValue {
  const quota = (): RestrictedValue => ({ IMMORTAL: 1n, DOUBLE_START: 1n, EIGHTWAY: 1n });
  return {
    boardSize: 19n,
    initialQuotas: { BLACK: quota(), WHITE: quota() },
  };
}

export interface GatewayClientOptions {
  url: string;
  store: SessionStore;
  socketFactory: WebSocketFactory;
  // requestId/commandId namespace; must satisfy the bounded ASCII grammar.
  clientToken?: string;
}

export class ClientClosedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ClientClosedError";
  }
}

export class ClientTransportError extends Error {
  readonly errorClass: string;
  constructor(errorClass: string, message: string) {
    super(message);
    this.name = "ClientTransportError";
    this.errorClass = errorClass;
  }
}

export class GatewayClient {
  #modelRequests = new Map<string, { resolve: (enabled: boolean) => void; reject: (error: Error) => void }>();

  controlModel(enabled: boolean | null): Promise<boolean> {
    const socket = this.#socket;
    if (socket === null) return Promise.reject(new ClientClosedError("socket is not open"));
    const requestId = this.#nextRequestId();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { this.#modelRequests.delete(requestId); reject(new Error("模型操作超时，请重试")); }, 35000);
      this.#modelRequests.set(requestId, {
        resolve: (value) => { clearTimeout(timer); resolve(value); },
        reject: (error) => { clearTimeout(timer); reject(error); },
      });
      try { socket.send(JSON.stringify({ schemaVersion: "collapse-model-control-request-v1", sessionId: this.store.sessionId, requestId, enabled })); }
      catch (error) { clearTimeout(timer); this.#modelRequests.delete(requestId); reject(error); }
    });
  }

  #rejectModelRequests() {
    for (const pending of this.#modelRequests.values()) pending.reject(new ClientClosedError("连接已断开"));
    this.#modelRequests.clear();
  }
  readonly url: string;
  readonly store: SessionStore;

  #socketFactory: WebSocketFactory;
  #clientToken: string;
  #socket: WebSocketLike | null = null;
  #requestCounter = 0;
  #commandCounter = 0;
  #unsubscribe: (() => void) | null = null;
  // Resubmission is dirty-flag driven: armed by every connect (unknown-fate
  // commands from the previous socket) and by every DEADLINE verdict on the
  // live connection (a durable commit may have crossed the ACK deadline
  // without publishing — the client re-covers via snapshot, then resubmits
  // the stored bytes for the redelivered receipt).
  #resubmitDirty = false;
  // Set by a DEADLINE verdict until the re-covering snapshot arrives: the
  // resubmission flush waits for the post-deadline baseline.
  #awaitingRecCover = false;
  // The re-cover snapshot's requestId, so its transportError/close releases
  // the gate instead of wedging the flush forever.
  #recCoverRequestId: string | null = null;

  #bindRequestId: string | null = null;
  #bindResolve: ((bound: SessionBoundPayload) => void) | null = null;
  #bindReject: ((error: Error) => void) | null = null;
  // submitCommand requestId → commandId (transportError routing).
  #commandRequests = new Map<string, string>();

  constructor(options: GatewayClientOptions) {
    this.url = options.url;
    this.store = options.store;
    this.#socketFactory = options.socketFactory;
    this.#clientToken = options.clientToken ?? "web";
    if (!ID_GRAMMAR.test(this.#clientToken))
      throw new RangeError("clientToken violates the bounded ASCII identifier grammar");
    this.#unsubscribe = this.store.subscribe(() => this.#maybeResubmit());
  }

  // Opens the socket and binds the session: createSession on the first
  // connect, bindSession with the stored cursor afterwards. Resolves with
  // the sessionBound payload; rejects on transportError or socket close.
  connect(): Promise<SessionBoundPayload> {
    if (this.store.broken !== null)
      return Promise.reject(new ClientTransportError("PROTOCOL", `store is broken: ${this.store.broken}`));
    // A new connect supersedes any pending bind of the previous generation:
    // reject it so its promise cannot hang, and retire the old socket's
    // in-flight commands to "unknown" here — the old socket's late close
    // event is generation-guarded away, so markClosed never runs for it.
    this.#rejectBind(new ClientClosedError("superseded by a new connect"));
    this.closeSocket();
    this.store.markConnecting();
    this.#resubmitDirty = true;
    this.#awaitingRecCover = false;
    this.#recCoverRequestId = null;
    const socket = this.#socketFactory(this.url);
    this.#socket = socket;
    const promise = new Promise<SessionBoundPayload>((resolve, reject) => {
      this.#bindResolve = resolve;
      this.#bindReject = reject;
    });
    // Every listener is generation-bound: a late event from a socket this
    // client has already replaced must not touch the new connection's state.
    socket.addEventListener("open", () => {
      if (this.#socket !== socket) return;
      try {
        this.#sendBind();
      } catch (error) {
        this.#rejectBind(error instanceof Error ? error : new Error(String(error)));
      }
    });
    socket.addEventListener("message", (event: { data?: unknown }) => {
      if (this.#socket !== socket) return;
      this.#onMessage(event.data);
    });
    socket.addEventListener("close", () => {
      if (this.#socket !== socket) return;
      // Detach first: markClosed's emit can trigger a resubmission flush,
      // and a CLOSING/CLOSED browser WebSocket silently drops send() — the
      // command would be marked inflight with its bytes never sent.
      this.#socket = null;
      this.#rejectModelRequests();
      this.store.markClosed();
      this.#awaitingRecCover = false;
      this.#recCoverRequestId = null;
      this.#rejectBind(new ClientClosedError("socket closed before sessionBound"));
      this.#commandRequests.clear();
    });
    socket.addEventListener("error", () => {
      // The close event follows; nothing to decide here.
    });
    return promise;
  }

  closeSocket(): void {
    const socket = this.#socket;
    if (socket === null) return;
    // Retire the socket BEFORE close(): the generation guard makes its late
    // close event a no-op, so an explicit close must run the natural close
    // handler's settlement itself — otherwise a close/dispose during a
    // pending bind would leave connect() waiting forever and strand the
    // re-cover/command bookkeeping until the next connect.
    this.#socket = null;
    socket.close();
    this.#rejectModelRequests();
    this.store.markClosed();
    this.#awaitingRecCover = false;
    this.#recCoverRequestId = null;
    this.#rejectBind(new ClientClosedError("socket closed before sessionBound"));
    this.#commandRequests.clear();
  }

  dispose(): void {
    this.closeSocket();
    if (this.#unsubscribe !== null) {
      this.#unsubscribe();
      this.#unsubscribe = null;
    }
  }

  #sendBind(): void {
    const epoch = this.store.streamEpoch;
    if (epoch === null) {
      this.#bindRequestId = this.#sendRequest("createSession", {
        ruleset: {
          rulesetId: PUBLIC_RULESET.rulesetId,
          semanticVersion: PUBLIC_RULESET.semanticVersion,
          descriptorSha256: PUBLIC_RULESET.descriptorSha256,
        },
        gameConfig: officialGameConfig(),
      });
      return;
    }
    // A client without a baseline has no usable cursor: echo null (server
    // reads cursor 0) so the fresh-snapshot baseline is always served. A
    // positive cursor is only meaningful together with the baseline it was
    // built on.
    this.#bindRequestId = this.#sendRequest("bindSession", {
      lastSeenSeq: this.store.baselinePending ? null : BigInt(this.store.lastSeq),
      streamEpoch: epoch,
    });
  }

  #nextRequestId(): string {
    return `${this.#clientToken}-r${++this.#requestCounter}`;
  }

  #nextCommandId(): string {
    return `${this.#clientToken}-c${++this.#commandCounter}`;
  }

  #sendRequest(type: RequestType, payload: RestrictedValue): string {
    const socket = this.#socket;
    if (socket === null) throw new ClientClosedError("socket is not open");
    const requestId = this.#nextRequestId();
    const envelope: RestrictedValue = {
      schemaVersion: REQUEST_SCHEMA,
      envelopeVersion: BigInt(ENVELOPE_VERSION),
      sessionId: this.store.sessionId,
      requestId,
      type,
      payload,
    };
    socket.send(canonicalizeRestricted(envelope));
    return requestId;
  }

  // --- commands -----------------------------------------------------------

  // Builds, registers, and submits an ACTION command. Returns commandId.
  // Requires a baseline (expectedRevision/Cursor are read from the tip).
  submitAction(actor: "BLACK" | "WHITE", actionId: number, kind: ActionKind | "PASS"): string {
    const tip = this.store.tip;
    if (tip === null)
      throw new ClientClosedError("no baseline state; the command would carry no position");
    // kind-major pairing is the client's own guarantee: the canonical bytes
    // it produces must never carry a mismatched actionId/kind pair.
    if (kindOf(actionId) !== kind)
      throw new RangeError(`actionId ${actionId} is not a ${kind} action`);
    const commandId = this.#nextCommandId();
    const command: RestrictedValue = {
      schemaVersion: COMMAND_SCHEMA,
      canonicalizationProfile: CANONICALIZATION_PROFILE,
      commandId,
      ruleset: {
        rulesetId: PUBLIC_RULESET.rulesetId,
        semanticVersion: PUBLIC_RULESET.semanticVersion,
        descriptorSha256: PUBLIC_RULESET.descriptorSha256,
      },
      expectedRevision: BigInt(tip.revision),
      expectedSemanticEventCursor: BigInt(tip.semanticEventCursor),
      intent: {
        kind: "ACTION",
        actor,
        action: {
          schemaVersion: ACTION_SCHEMA,
          actionId: BigInt(actionId),
          kind,
        },
      },
    };
    return this.#submitCommand(commandId, canonicalizeRestricted(command));
  }

  submitTermination(actor: "BLACK" | "WHITE", kind: "RESIGNATION" | "TIMEOUT"): string {
    const tip = this.store.tip;
    if (tip === null)
      throw new ClientClosedError("no baseline state; the command would carry no position");
    const commandId = this.#nextCommandId();
    const command: RestrictedValue = {
      schemaVersion: COMMAND_SCHEMA,
      canonicalizationProfile: CANONICALIZATION_PROFILE,
      commandId,
      ruleset: {
        rulesetId: PUBLIC_RULESET.rulesetId,
        semanticVersion: PUBLIC_RULESET.semanticVersion,
        descriptorSha256: PUBLIC_RULESET.descriptorSha256,
      },
      expectedRevision: BigInt(tip.revision),
      expectedSemanticEventCursor: BigInt(tip.semanticEventCursor),
      intent: { kind, actor },
    };
    return this.#submitCommand(commandId, canonicalizeRestricted(command));
  }

  #submitCommand(commandId: string, canonicalJson: string): string {
    this.store.addPending(commandId, canonicalJson);
    try {
      const requestId = this.#sendRequest("submitCommand", { commandCanonicalJson: canonicalJson });
      this.#commandRequests.set(requestId, commandId);
      return commandId;
    } catch (error) {
      // The send never happened: the command is of unknown fate only to the
      // network, so mark it resubmittable immediately (the next flush or
      // reconnect carries it) instead of parking it inflight forever.
      const entry = this.store.pending.get(commandId);
      if (entry !== undefined) entry.status = "unknown";
      this.#resubmitDirty = true;
      throw error;
    }
  }

  // --- read models --------------------------------------------------------

  requestSnapshot(): string {
    return this.#sendRequest("requestSnapshot", Object.create(null) as RestrictedValue);
  }

  requestLegalMask(): string {
    return this.#sendRequest("legalMaskRead", Object.create(null) as RestrictedValue);
  }

  requestExportLog(): string {
    return this.#sendRequest("exportLog", Object.create(null) as RestrictedValue);
  }

  ping(): string {
    return this.#sendRequest("ping", Object.create(null) as RestrictedValue);
  }

  analysisStart(maxTotalPlayouts: number, maxDurationSeconds: number): string {
    return this.#sendRequest("analysisStart", {
      maxTotalPlayouts: BigInt(maxTotalPlayouts),
      maxDurationSeconds: BigInt(maxDurationSeconds),
    });
  }

  analysisStop(): string {
    return this.#sendRequest("analysisStop", Object.create(null) as RestrictedValue);
  }

  // --- SGF import (untrusted intent replay, M6-D4) -------------------------

  // Replays parsed SGF mainline moves as NORMAL/PASS intents against THIS
  // session, one at a time, waiting for each authoritative receipt. The
  // imported text never patches state: every intent is a fresh command the
  // C++ reducer validates; the first rejection stops the replay. Returns a
  // summary; progress also lands on store.importStatus.
  async importReplay(moves: SgfMove[]): Promise<{ completed: number; total: number; stopped: string | null }> {
    // The replay entry point re-enforces the parser's move cap: intents may
    // cross the sessionStorage trust boundary without re-parsing.
    if (moves.length > SGF_MAX_MOVES) {
      this.store.setImportStatus(`重放拒绝:超过 ${SGF_MAX_MOVES} 手上限`);
      return { completed: 0, total: moves.length, stopped: `超过 ${SGF_MAX_MOVES} 手上限` };
    }
    let completed = 0;
    this.store.setImportStatus(`重放 0/${moves.length}`);
    const finish = (stopped: string | null) => {
      this.store.setImportStatus(
        stopped === null
          ? `重放完成 ${completed}/${moves.length}`
          : `重放中止(${completed}/${moves.length}): ${stopped}`,
      );
      return { completed, total: moves.length, stopped };
    };
    for (const move of moves) {
      if (this.store.broken !== null)
        return finish(`协议失败关闭: ${this.store.broken}`);
      if (this.store.phase !== "live" || this.store.tip === null)
        return finish("会话不处于 live");
      const pendingBefore = new Set(this.store.pending.keys());
      const actionId = importActionId(move);
      let commandId: string;
      try {
        commandId = this.submitAction(
          move.color, actionId, move.point === null ? "PASS" : "NORMAL",
        );
      } catch (error) {
        // A synchronous send failure still registered the intent as a
        // pending unknown-fate command (single-threaded: the pending-set
        // delta is exactly this command). Do NOT stop here — stopping would
        // strand the import status while the resubmission machinery may
        // still commit the move. Wait for its authoritative fate like any
        // other unknown-fate command.
        const added = [...this.store.pending.keys()].filter((k) => !pendingBefore.has(k));
        if (added.length !== 1)
          return finish(error instanceof Error ? error.message : String(error));
        commandId = added[0]!;
        this.store.setImportStatus(
          `重放 ${completed}/${moves.length}(发送失败,等待重连重提交)`,
        );
      }
      let receipt: CommandReceipt;
      try {
        receipt = await this.#awaitReceipt(commandId);
      } catch (error) {
        return finish(error instanceof Error ? error.message : String(error));
      }
      if (!receipt.accepted) {
        const why = receipt.error !== null
          ? `${receipt.error.category}/${receipt.error.code}`
          : `传输 ${receipt.transportErrorClass}`;
        return finish(`第 ${completed + 1} 手被拒绝: ${why}`);
      }
      completed++;
      this.store.setImportStatus(`重放 ${completed}/${moves.length}`);
    }
    return finish(null);
  }

  // Resolves with the command's authoritative receipt. The idempotent
  // resubmission machinery owns reconnects/DEADLINE, so there is no local
  // timeout: a broken store is the only terminal failure.
  #awaitReceipt(commandId: string): Promise<CommandReceipt> {
    const existing = this.store.receipts.get(commandId);
    if (existing !== undefined) return Promise.resolve(existing);
    return new Promise((resolve, reject) => {
      const unsubscribe = this.store.subscribe(() => {
        if (this.store.broken !== null) {
          unsubscribe();
          reject(new ClientTransportError("PROTOCOL", this.store.broken));
          return;
        }
        const receipt = this.store.receipts.get(commandId);
        if (receipt !== undefined) {
          unsubscribe();
          resolve(receipt);
        }
      });
    });
  }

  // --- inbound ------------------------------------------------------------

  #onMessage(data: unknown): void {
    if (typeof data !== "string") {
      this.#breakAndReject("inbound frame is not text");
      return;
    }
    let envelope;
    try {
      const control = JSON.parse(data);
      if (control?.schemaVersion === "collapse-model-control-event-v1") {
        parseRestrictedJson(data); // Reject duplicate keys and non-profile JSON.
        if (JSON.stringify(Object.keys(control).sort()) !== JSON.stringify(["enabled", "error", "requestId", "schemaVersion", "sessionId"]) ||
            typeof control.requestId !== "string" || control.requestId.length > 128 || !ID_GRAMMAR.test(control.requestId) ||
            control.sessionId !== this.store.sessionId || typeof control.enabled !== "boolean" ||
            !(control.error === null || typeof control.error === "string")) throw new Error("invalid model status");
        const pending = this.#modelRequests.get(control.requestId);
        // A timed-out request can legitimately finish late; never install it.
        if (pending) {
          this.#modelRequests.delete(control.requestId);
          if (control.error === null) pending.resolve(control.enabled);
          else pending.reject(new ClientTransportError(control.error, control.error));
        }
        return;
      }
      envelope = parseInboundEnvelope(data);
    } catch {
      // Unparseable/unrecognized envelope: fail closed at the store.
      this.#breakAndReject("inbound envelope failed validation");
      return;
    }
    if (envelope.kind === "connectionEvent") {
      const { requestId, type, payload } = envelope.event;
      if (type === "transportError") {
        let errorClass: string;
        try {
          errorClass = validateTransportErrorPayload(payload).errorClass;
        } catch (error) {
          if (error instanceof WireError) {
            this.#breakAndReject(error.message);
            return;
          }
          throw error;
        }
        if (requestId !== null) {
          if (requestId === this.#recCoverRequestId) {
            // The re-cover snapshot itself failed transport: re-cover is
            // best-effort — the idempotent resubmission receipt resolves the
            // command's fate, so release the gate and let the flush proceed.
            this.#recCoverRequestId = null;
            this.#awaitingRecCover = false;
          }
          if (requestId === this.#bindRequestId) {
            this.#rejectBind(new ClientTransportError(errorClass, `bind rejected: ${errorClass}`));
          }
          const commandId = this.#commandRequests.get(requestId);
          if (commandId !== undefined) {
            this.#commandRequests.delete(requestId);
            this.store.commandTransportRejected(commandId, errorClass);
            if (errorClass === "DEADLINE") {
              // The commit may be durable without a published commandResult:
              // re-cover the state, then resubmit for the redelivered receipt.
              this.#resubmitDirty = true;
              this.#awaitingRecCover = true;
              this.#requestReCover();
              if (this.#recCoverRequestId === null) {
                // The send never happened (socket racing away): drop the gate
                // now; the next connect re-arms the dirty flag. A pull
                // already in flight releases the gate when it lands.
                this.#awaitingRecCover = false;
              }
            }
          }
        }
      }
      // sessionBound is the answer to THIS connection's bind, exactly once;
      // an unmatched requestId is a protocol violation, not a usable bind.
      if (type === "sessionBound" && requestId !== this.#bindRequestId) {
        this.#breakAndReject("sessionBound carries an unmatched requestId");
        return;
      }
    }
    const prevSeq = envelope.kind === "sessionEvent" ? this.store.lastSeq : -1;
    if (envelope.kind === "sessionEvent" && envelope.event.type === "snapshot") {
      this.#awaitingRecCover = false;
      this.#recCoverRequestId = null;
    }
    this.store.applyInbound(envelope);
    // Any break surfaced by the store while a bind is still pending must
    // reject the bind promise too — a hung connect() is never a final state.
    if (this.store.broken !== null)
      this.#rejectBind(new ClientTransportError("PROTOCOL", this.store.broken));
    // A live notice ahead of the local tip (e.g. recovery completed over a
    // silent DEADLINE commit) parks the store in syncing; pull a covering
    // snapshot so it converges without waiting for the next bind. Only an
    // event the store actually APPLIED (streamSeq advanced) may trigger the
    // pull — a duplicate delivery carries no outbound side effect.
    if (
      envelope.kind === "sessionEvent" &&
      envelope.event.type === "sessionState" &&
      this.store.lastSeq > prevSeq &&
      this.store.broken === null &&
      this.store.phase === "syncing" &&
      !this.store.baselinePending
    )
      this.#requestReCover();
    if (
      envelope.kind === "sessionEvent" &&
      envelope.event.type === "commandResult" &&
      this.store.broken === null
    ) {
      // Receipt fulfilled: release the requestId→commandId mapping so the
      // table does not grow with every completed command.
      for (const [rid, cid] of this.#commandRequests)
        if (!this.store.pending.has(cid)) this.#commandRequests.delete(rid);
    }
    if (
      envelope.kind === "connectionEvent" &&
      envelope.event.type === "sessionBound" &&
      this.#bindResolve !== null
    ) {
      // The store validated the payload; a broken store rejects the bind.
      if (this.store.broken !== null) {
        this.#rejectBind(new ClientTransportError("PROTOCOL", this.store.broken));
        return;
      }
      const bound = {
        revision: this.store.serverRevision,
        created: this.store.created,
        streamEpoch: this.store.streamEpoch!,
      };
      const resolve = this.#bindResolve;
      this.#bindResolve = null;
      this.#bindReject = null;
      this.#bindRequestId = null;
      // A bound ahead of the local tip means a commit published nothing the
      // tail can carry (silent DEADLINE commit); pull a snapshot so the
      // syncing store can converge to live.
      const tip = this.store.tip;
      if (!this.store.baselinePending && tip !== null && bound.revision > tip.revision)
        this.#requestReCover();
      resolve(bound);
    }
  }

  #breakAndReject(message: string): void {
    this.store.breakProtocol(message);
    this.#rejectModelRequests();
    this.#rejectBind(new ClientTransportError("PROTOCOL", message));
  }

  // Single-flight re-cover pull: at most one covering snapshot request per
  // connection generation, shared by the bound-ahead, live-ahead, and
  // DEADLINE paths. Released by any arriving snapshot, the request's own
  // transportError, or a socket close — never re-armed while in flight, so a
  // stale tail cannot amplify into a snapshot storm against the ring.
  #requestReCover(): void {
    if (this.#recCoverRequestId !== null) return;
    try {
      this.#recCoverRequestId = this.requestSnapshot();
    } catch {
      // Socket racing away — the next connect re-arms via the dirty flag.
    }
  }

  #rejectBind(error: Error): void {
    const reject = this.#bindReject;
    this.#bindReject = null;
    this.#bindResolve = null;
    this.#bindRequestId = null;
    if (reject !== null) reject(error);
  }

  // After a rebind's baseline lands, resubmit every unreceipted command
  // with its stored canonical bytes (idempotent — the receipt arrives as a
  // redelivered commandResult, or a clean rejection after a position move).
  #maybeResubmit(): void {
    if (this.store.broken !== null) return;
    if (this.store.baselinePending) return;
    if (this.#awaitingRecCover) return;
    if (!this.#resubmitDirty) return;
    if (this.#socket === null) return;
    this.#resubmitDirty = false;
    for (const entry of this.store.pending.values()) {
      if (entry.status !== "unknown") continue;
      try {
        const requestId = this.#sendRequest("submitCommand", {
          commandCanonicalJson: entry.canonicalJson,
        });
        this.#commandRequests.set(requestId, entry.commandId);
        entry.status = "inflight";
      } catch {
        entry.status = "unknown"; // stays for the next reconnect
        this.#resubmitDirty = true;
      }
    }
  }
}
